import { A } from '@ember/array';
import { computed } from '@ember/object';
import { union, filter } from '@ember/object/computed';
import { attr, belongsTo, hasMany } from '@ember-data/model';
import { memberAction } from 'ember-api-actions';
import { BILLABLE_RECORD } from "./billable-record";
import { PROJECT_LINE_ITEM } from "./project-line-item";
import Moment from 'moment';
import { SH_BILLABLE_LINE_ITEM } from "./sh-billable-line-item";
import ShModel from './sh-model';

const STATUS = {
  PENDING: '0',
  QUOTE_REQUESTED: '0.5',
  PUBLISHER_APPROVED: '1',
  PRODUCER_APPROVED: '2',
  PUBLISHER_APPROVED_PRICE: '2.5',
  ACTIVE: '3',
  COMPLETE_PRODUCER: '3.5',
  COMPLETE_PUBLISHER: '3.75',
  COMPLETED: '4',
  PAID: '5',
  DECLINED: '-1',
  CANCEL_PENDING: '-2',
  CANCELLED: '-3',
};

const SOURCE = {
  HIRE: 1,
  QUOTE: 2,
};

const SIMPLE_STATUS = {
  ACTIVE: 'active',
  ARCHIVED: 'archived',
  CANCELLED: 'cancelled',
  CANCELLED_BUYER: 'cancelled_buyer',
  COMPLETE: 'complete',
  COMPLETE_PRODUCER: 'complete_producer',
  DECLINED: 'declined',
  FINAL: 'final',
  HIRED: 'hired',
  IN_PROGRESS: 'in_progress',
  IN_PROGRESS_BUYER: 'in_progress_buyer',
  NEEDS_ACTION_BUYER: 'needs_action_buyer',
  NEEDS_ACTION_SELLER: 'needs_action_seller',
  STARTED: 'started',
  NOT_STARTED: 'not_started',
  PAID: 'paid',
  PENDING: 'pending',
  PENDING_BUYER: 'pending_buyer',
  PENDING_SELLER: 'pending_seller',
};

export {
  STATUS as APPLICATION_STATUS,
  SIMPLE_STATUS as APPLICATION_SIMPLE_STATUS,
  SOURCE as APPLICATION_SOURCE,
};

/**
 * These are the mappings from combined (SIMPLE) statuses to the actual application statuses.
 *
 * @type {Object}
 */
const SIMPLE_STATUS_MAPPING = {
  [SIMPLE_STATUS.ACTIVE]: [STATUS.ACTIVE],
  [SIMPLE_STATUS.ARCHIVED]: [
    STATUS.CANCELLED,
    STATUS.COMPLETED,
    STATUS.PAID,
    STATUS.DECLINED,
  ],
  [SIMPLE_STATUS.CANCELLED]: [STATUS.CANCELLED],
  [SIMPLE_STATUS.CANCELLED_BUYER]: [
    STATUS.CANCELLED,
    STATUS.CANCEL_PENDING
  ],
  [SIMPLE_STATUS.COMPLETE_PRODUCER]: [STATUS.COMPLETE_PRODUCER],
  [SIMPLE_STATUS.DECLINED]: [
    STATUS.DECLINED,
    STATUS.CANCEL_PENDING,
    STATUS.CANCELLED,
  ],
  [SIMPLE_STATUS.FINAL]: [
    STATUS.CANCELLED,
    STATUS.COMPLETED,
    STATUS.PAID,
  ],
  [SIMPLE_STATUS.HIRED]: [
    STATUS.ACTIVE,
    STATUS.CANCEL_PENDING,
    STATUS.COMPLETED,
    STATUS.PAID,
    STATUS.COMPLETE_PRODUCER,
    STATUS.COMPLETE_PUBLISHER,
  ],
  [SIMPLE_STATUS.IN_PROGRESS]: [
    STATUS.ACTIVE,
    STATUS.CANCEL_PENDING,
    STATUS.COMPLETE_PRODUCER,
    STATUS.COMPLETE_PUBLISHER,
    STATUS.PRODUCER_APPROVED,
    STATUS.PUBLISHER_APPROVED,
    STATUS.PUBLISHER_APPROVED_PRICE,
  ],
  [SIMPLE_STATUS.IN_PROGRESS_BUYER]: [
    STATUS.PUBLISHER_APPROVED,
    STATUS.PRODUCER_APPROVED,
    STATUS.PUBLISHER_APPROVED_PRICE,
    STATUS.ACTIVE,
    STATUS.COMPLETE_PRODUCER,
    STATUS.COMPLETE_PUBLISHER,
  ],
  [SIMPLE_STATUS.NEEDS_ACTION_BUYER]: [
    STATUS.PENDING,
    STATUS.PRODUCER_APPROVED,
    STATUS.COMPLETE_PRODUCER,
  ],
  [SIMPLE_STATUS.NEEDS_ACTION_SELLER]: [
    STATUS.QUOTE_REQUESTED,
    STATUS.PUBLISHER_APPROVED,
    STATUS.PUBLISHER_APPROVED_PRICE,
    STATUS.COMPLETE_PUBLISHER,
    STATUS.CANCEL_PENDING,
  ],
  [SIMPLE_STATUS.PAID]: [STATUS.PAID],
  [SIMPLE_STATUS.PENDING]: [
    STATUS.PENDING,
    STATUS.QUOTE_REQUESTED,
    STATUS.PRODUCER_APPROVED,
    STATUS.PUBLISHER_APPROVED,
    STATUS.PUBLISHER_APPROVED_PRICE,
  ],
  [SIMPLE_STATUS.PENDING_BUYER]: [
    STATUS.PENDING,
    STATUS.PUBLISHER_APPROVED,
    STATUS.PUBLISHER_APPROVED_PRICE,
  ],
  [SIMPLE_STATUS.PENDING_SELLER]: [
    STATUS.PENDING,
    STATUS.QUOTE_REQUESTED,
  ],
  [SIMPLE_STATUS.REACHED_ACTIVE]: [
    STATUS.ACTIVE,
    STATUS.COMPLETE_PRODUCER,
    STATUS.COMPLETE_PUBLISHER,
    STATUS.CANCEL_PENDING,
    STATUS.CANCELLED,
    STATUS.COMPLETED,
    STATUS.PAID,
  ],
};

export const APPLICATION = {
  STATUS,
  SIMPLE_STATUS,
  SIMPLE_STATUS_MAPPING,
};

const Application = ShModel.extend({
  activeAt: attr('date'),
  canEditUntil: attr('date'),
  completedAt: attr('date'),
  createdAt: attr('date'),
  direct: attr('boolean'),
  expiresAt: attr('date'),
  expediteCommitment: attr('boolean'),
  maxExpectedPayoutDays: attr('number'),
  isAdvanceSafe: attr('boolean'),
  isAllowExpenses: attr('boolean'),
  isFavorite: attr('boolean'),
  isInRemediation: attr('boolean'),
  isRead: attr('boolean'),
  lastActivity: attr('date'),
  paymentFinalized: attr('boolean'),
  price: attr(),
  cancellationPrice: attr('number'),
  remediationBeginsAt: attr('date'),
  select: attr('boolean'),
  sellerFeesCovered: attr('boolean'),
  status: attr('string'),
  statusColor: attr('string'),
  statusText: attr('string'),
  statusEnum: attr('string'),
  actionText: attr('string'),
  source: attr('number'),
  storyCode: attr('string'),
  title: attr('string'),
  type: attr('number'),
  updatedAt: attr('date'),

  // Relationships
  actions: hasMany('action', {
    async: false,
    inverse: 'actionable',
  }),
  applicationDeliverables: hasMany('application-deliverable', { async: false }),
  applicationDetails: belongsTo('application-detail', { async: false }),
  applicationSchedules: hasMany('application-schedule', { async: false }),
  assets: hasMany('asset', {
    async: false,
    polymorphic: true
  }),
  assignmentQuestionResponses: hasMany('assignment-question-response', { async: false }),
  brand: belongsTo('brand', { async: false }),
  buyer: belongsTo('user', { async: false, inverse: null }),
  company: belongsTo('company', { async: false }),
  companyTeam: belongsTo('companyTeam', { async: false }),
  costCode: belongsTo('costCode', { async: false }),
  feeStructure: belongsTo('feeStructure', { async: false }),
  followers: hasMany('user', { async: false }),
  internalReview: belongsTo('review', { async: false }),
  legalAgreements: hasMany('legalAgreement', { async: false, inverse: 'related'} ),
  location: belongsTo('location', { async: false }),
  seller: belongsTo('user', { async: false, inverse: null }),
  applicationSellerReviews: hasMany('applicationSellerReview', { async: false }),
  services: hasMany('service', { async: false }),
  assignment: belongsTo('assignment', { async: false }),
  thread: belongsTo('thread', { async: false }),
  pitch: belongsTo('pitch', { async: false }),
  projectdigest: belongsTo('projectdigest', { async: false }),
  projectLineItems: hasMany('projectLineItem', { async: false }),
  shInvoices: hasMany('shInvoice', { async: false }),
  shPayments: hasMany('shPayment', { async: false }),
  shInvoiceLineItems: hasMany('shInvoiceLineItem', { async: false }),
  shPaymentLineItems: hasMany('shPaymentLineItem', { async: false }),

  // buyer advances info
  nextAdvancePayoutDate: attr('date'),
  nextAdvancePayoutUntil: attr('date'),

  /**
   * Convenience method to access the merged array of all billables
   * @return Ember.A(<BillableRecord>)
   */
  shBillables: union('shInvoices', 'shPayments'),

  /**
   * Returns the base ShInvoice for an Application
   * @return {ShInvoice}
   */
  baseShInvoice: computed('shInvoices.@each.type', function () {
    return this.get('shInvoices').findBy('type', BILLABLE_RECORD.TYPES.PROJECT);
  }),

  /**
   * Returns the base ShPayment for an Application
   * @return {ShPayment}
   */
  baseShPayment: computed('shPayments.@each.type', function () {
    return this.get('shPayments').findBy('type', BILLABLE_RECORD.TYPES.PROJECT);
  }),

  /**
   * Returns true if there is an advance that needs review.
   *
   * @type {Boolean}
   */
  isAdvancePending: computed(
    'advanceShPaymentNeedsAgreement',
    'advanceShInvoiceNeedsAgreement',
    function () {
      return !!this.get('advanceShPaymentNeedsAgreement')
        || !!this.get('advanceShInvoiceNeedsAgreement');
    }
  ),

  /**
   * Returns a related ShPayment if there is one that requires the given agreement.
   *
   * @param needsApproval
   * @param needsAgreement
   * @returns {undefined|ShPayment}
   */
  advanceShPaymentNeedsAgreement: computed(
    'shPayments.@each.{agreement,type}',
    function () {
      let shPayments = this.get('shPayments');
      if (!shPayments) {
        return undefined;
      }

      return shPayments.find((shPayment) => {
        return shPayment.get('type') === BILLABLE_RECORD.TYPES.PROJECT_ADVANCE
          && shPayment.get('agreement') !== BILLABLE_RECORD.AGREEMENT.AGREED;
      });
    }
  ),

  /**
   * Returns a related ShInvoice if there is one that requires the given agreement.
   *
   * @param needsApproval
   * @param needsAgreement
   * @returns {undefined|ShInvoice}
   */
  advanceShInvoiceNeedsAgreement: computed(
    'shInvoices.@each.{agreement,type}',
    function () {
      const shInvoices = this.shInvoices;
      if (!shInvoices) {
        return undefined;
      }

      return shInvoices.find((shInvoice) => {
        return shInvoice.type === BILLABLE_RECORD.TYPES.PROJECT_ADVANCE
          && shInvoice.agreement !== BILLABLE_RECORD.AGREEMENT.AGREED;
      });
    }
  ),

  /**
   * Array of expenses to show based off of whether the buyer or seller is viewing.
   *
   * @type {Ember.A<ProjectLineItem>}
   */
  expenseProjectLineItems: filter(
    'projectLineItems.@each.{amount,type}',
    function(projectLineItem, index, array) {
      return projectLineItem.get('type') === PROJECT_LINE_ITEM.TYPES.EXPENSE;
    }
  ),

  /**
   * @type {Number}     The total value of all expenses.
   */
  expensesTotalAmount: computed(
    'expenseProjectLineItems.@each.amount',
    function() {
      return this.get('expenseProjectLineItems').reduce((carry, expenseLineItem) => carry + expenseLineItem.get('amount'), 0);
    }
  ),

  /** @type {Number} */
  buyerFeesTotalAmount: computed(
    'shInvoices.@each.feeTotalAmount',
    function () {
      return this.get('shInvoices').reduce((carry, shInvoice) => {
        return carry + shInvoice.get('feeTotalAmount');
      }, 0);
    }
  ),

  /** @type {Number} */
  sellerFeesTotalAmount: computed(
    'shPayments.@each.feeTotalAmount',
    function () {
      return this.get('shPayments').reduce((carry, shPayment) => {
        return carry + shPayment.get('feeTotalAmount');
      }, 0);
    }
  ),

  /** @type {Number} */
  shInvoicesTotalAmount: computed(
    'shInvoices.@each.amount',
    function () {
      return this.get('shInvoices').reduce((carry, shInvoice) => {
        return carry + shInvoice.get('amount');
      }, 0);
    }
  ),

  /** @type {Number} */
  shPaymentsTotalAmount: computed(
    'shPayments.@each.amount',
    function () {
      return this.get('shPayments').reduce((carry, shPayment) => {
        return carry + shPayment.get('amount');
      }, 0);
    }
  ),

  /**
   * @type {Ember.A}     Array of consolidated fee objects
   */
  consolidatedBuyerFees: computed(
    'shInvoiceLineItems.@each.{amount,type}',
    function() {
      return this.consolidateBillableFees(this.get('shInvoiceLineItems'));
    }
  ),

  /**
   * @type {Ember.A}     Array of consolidated fee objects
   */
  consolidatedSellerFees: computed(
    'shPaymentLineItems.@each.{amount,type}',
    function() {
      return this.consolidateBillableFees(this.get('shPaymentLineItems'));
    }
  ),

  /**
   * INTERNAL METHOD
   *
   * @param shBillableLineItems
   * @returns {Ember.A}
   */
  consolidateBillableFees(shBillableLineItems) {
    const feesArray = A();

    SH_BILLABLE_LINE_ITEM.LINE_ITEM_FEE_TYPES.forEach((feeType) => {
      const amount = shBillableLineItems.reduce((billableCarry, shBillableLineItem) => {
        return billableCarry + (shBillableLineItem.get('type') === feeType ? shBillableLineItem.get('amount') : 0);
      }, 0);

      if (amount !== 0) {
        feesArray.pushObject({
          amount: amount,
          description: SH_BILLABLE_LINE_ITEM.TYPE_DESCRIPTIONS[feeType],
          type: feeType
        });
      }
    });

    return feesArray;
  },

  /**
   * This will return true if the advance date has NOT expired.
   *
   * @return {boolean}
   * @private
   */
  isAdvanceDateValid() {
    let now = new Date(),
      validUntilDate = this.get('nextAdvancePayoutUntil');

    if (now.getTime() > validUntilDate.getTime()) {
      return false;
    }

    return true;
  },

  /**
   * True if we have an expiresAt AND it is within one day.
   *
   * @type {boolean}
   */
  isExpiredTimeWithinOneDay: computed('expiresAt', function() {
    let expiresAt = this.get('expiresAt');
    if (!expiresAt) {
      return false;
    }

    let now = Moment(),
      expiresAtMoment = Moment(expiresAt);

    return expiresAtMoment.diff(now, 'hours') < 24;
  }),

  /**
   * @type {Boolean}
   */
  isPending: computed('status', function() {
    return this.get('status') === APPLICATION.STATUS.PENDING;
  }),

  /**
   * @type {Boolean}    True if this application does NOT have an offer yet.
   */
  isNeedsOffer: computed(
  'projectLineItems.@each.{amount,type}',
  function() {
    let projectLineItems = this.get('projectLineItems');
    if (!projectLineItems) {
      return false;
    }

    let baseProjectLineItem = projectLineItems.find(lineItem => lineItem.get('type') === PROJECT_LINE_ITEM.TYPES.BASE);
    return !baseProjectLineItem || !baseProjectLineItem.get('amount');
  }),

  isUnread: computed('isRead', function() {
    return !this.get('isRead');
  }),

  /** @type {string} */
  locationText: computed('location.formattedAddress', function() {
    const { location } = this;

    if (location) {
      return location.formattedAddress
    }

    return 'Global';
  }),

  scheduleItems: computed('applicationSchedules.@each.date', function() {
    return this.applicationSchedules.sortBy('date');
  }),

  addFavorite: memberAction({ path: 'set-favorite', type: 'post'}),
  removeFavorite: memberAction({ path: 'unset-favorite', type: 'post'}),
});

export default Application;
